import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getBanners } from "../../../../api/calls/client/banners";
import { getCategories } from "../../../../api/calls/product/categories";
import { getProducts } from "../../../../api/calls/product/products";
import BGImage from "../../../../assets/bgImage.png";
const CURRENT_PAGE = 1;
const RECORDS_PER_PAGE = 20;
const CATEGORY_RECORDS_PER_PAGE = 10;
const HOME_BANNER_PAGE_SIZE = 10;
const HOME_CAROUSEL_CUSTOM_ID = "home-carousel";
const DEFAULT_BANNER_SLIDE = {
    _id: "first-slide",
    title: "Heading",
    subtitle: "Welcome to SCEE",
    imageUrl: BGImage,
    layout: "cover",
};
export function useHomeState() {
    const [categories, setCategories] = useState([]);
    const [slides, setSlides] = useState([DEFAULT_BANNER_SLIDE]);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [slideDirection, setSlideDirection] = useState(1);
    const [bannerPage, setBannerPage] = useState(0);
    const [hasMoreBanners, setHasMoreBanners] = useState(true);
    const [recommendedItems, setRecommendedItems] = useState([]);
    const [supportingDocuments, setSupportingDocuments] = useState([]);
    const [isLoadingProducts, setIsLoadingProducts] = useState(true);
    const [isLoadingCategories, setIsLoadingCategories] = useState(true);
    const [isLoadingBanners, setIsLoadingBanners] = useState(true);
    const navigate = useNavigate();
    const isFetchingBannerRef = useRef(false);
    const slidesRef = useRef(slides);
    const currentSlideRef = useRef(currentSlide);
    const bannerPageRef = useRef(bannerPage);
    const hasMoreBannersRef = useRef(hasMoreBanners);
    const preloadedImageUrlsRef = useRef(new Set());
    useEffect(() => {
        slidesRef.current = slides;
    }, [slides]);
    useEffect(() => {
        currentSlideRef.current = currentSlide;
    }, [currentSlide]);
    useEffect(() => {
        bannerPageRef.current = bannerPage;
    }, [bannerPage]);
    useEffect(() => {
        hasMoreBannersRef.current = hasMoreBanners;
    }, [hasMoreBanners]);
    const applyDefaultBannerSlide = useCallback(() => {
        setSlides([DEFAULT_BANNER_SLIDE]);
        slidesRef.current = [DEFAULT_BANNER_SLIDE];
        setCurrentSlide(0);
        currentSlideRef.current = 0;
        setBannerPage(0);
        bannerPageRef.current = 0;
        setHasMoreBanners(false);
        hasMoreBannersRef.current = false;
    }, []);
    const mapBannerToSlide = useCallback((banner) => {
        return {
            _id: banner._id,
            title: banner.title || "Heading",
            subtitle: banner.description || "Welcome to SCEE",
            imageUrl: banner.image || BGImage,
            layout: "cover",
            label: banner.label,
            customId: banner.custom_id,
            linkType: banner.link_type,
            linkTarget: banner.link_target,
            createdAt: banner.createdAt,
            updatedAt: banner.updatedAt,
        };
    }, []);
    const preloadSlideImages = useCallback((slidesToPreload) => {
        for (const slide of slidesToPreload) {
            const imageUrl = slide.imageUrl;
            if (!imageUrl || preloadedImageUrlsRef.current.has(imageUrl)) {
                continue;
            }
            const img = new Image();
            img.src = imageUrl;
            preloadedImageUrlsRef.current.add(imageUrl);
        }
    }, []);
    const isBannerVisibleNow = useCallback((banner) => {
        if (banner.custom_id !== HOME_CAROUSEL_CUSTOM_ID) {
            return false;
        }
        const now = Date.now();
        const startTime = banner.start_date ? new Date(banner.start_date).getTime() : null;
        const endTime = banner.end_date ? new Date(banner.end_date).getTime() : null;
        if (startTime !== null && Number.isFinite(startTime) && now < startTime) {
            return false;
        }
        if (endTime !== null && Number.isFinite(endTime) && now > endTime) {
            return false;
        }
        return true;
    }, []);
    const fetchBannerPage = useCallback(async (page) => {
        if (isFetchingBannerRef.current) {
            return {
                slides: slidesRef.current,
                hasMore: hasMoreBannersRef.current,
                success: false,
            };
        }
        isFetchingBannerRef.current = true;
        setIsLoadingBanners(true);
        try {
            const response = await getBanners({
                active: true,
                sort_by: "sort_order",
                order_by: "asc",
                custom_id: HOME_CAROUSEL_CUSTOM_ID,
                page: page.toString(),
                size: HOME_BANNER_PAGE_SIZE.toString(),
            });
            if (!response.success) {
                return {
                    slides: slidesRef.current,
                    hasMore: hasMoreBannersRef.current,
                    success: false,
                };
            }
            const rawBanners = response.content?.banners ?? [];
            const visibleSlides = rawBanners.filter(isBannerVisibleNow).map(mapBannerToSlide);
            preloadSlideImages(visibleSlides);
            const existingSlides = slidesRef.current.filter((slide) => slide._id !== DEFAULT_BANNER_SLIDE._id);
            const seenIds = new Set(existingSlides.map((slide) => slide._id));
            const mergedSlides = [...existingSlides];
            for (const slide of visibleSlides) {
                if (!seenIds.has(slide._id)) {
                    mergedSlides.push(slide);
                    seenIds.add(slide._id);
                }
            }
            const totalRecords = Number(response.content?.count ?? 0);
            const totalPages = totalRecords > 0 ? Math.ceil(totalRecords / HOME_BANNER_PAGE_SIZE) : 0;
            const resolvedHasMore = totalPages > 0 ? page < totalPages : rawBanners.length === HOME_BANNER_PAGE_SIZE;
            const nextSlides = mergedSlides.length > 0 ? mergedSlides : slidesRef.current;
            setSlides(nextSlides);
            slidesRef.current = nextSlides;
            setBannerPage(page);
            bannerPageRef.current = page;
            setHasMoreBanners(resolvedHasMore);
            hasMoreBannersRef.current = resolvedHasMore;
            return {
                slides: nextSlides,
                hasMore: resolvedHasMore,
                success: true,
            };
        }
        finally {
            setIsLoadingBanners(false);
            isFetchingBannerRef.current = false;
        }
    }, [isBannerVisibleNow, mapBannerToSlide, preloadSlideImages]);
    useEffect(() => {
        const fetchCategories = async () => {
            const { success, content } = await getCategories({ page: CURRENT_PAGE.toString(), size: CATEGORY_RECORDS_PER_PAGE.toString() });
            if (success) {
                const { product_category_list } = content;
                const filtered_list = product_category_list?.filter((category) => !!category.is_online);
                setCategories(filtered_list);
            }
            else {
                setCategories([]);
            }
            setIsLoadingCategories(false);
        };
        const fetchProducts = async () => {
            const { success, content } = await getProducts({ page: CURRENT_PAGE.toString(), size: RECORDS_PER_PAGE.toString() });
            if (success) {
                const { product_list } = content;
                setRecommendedItems(product_list || []);
            }
            setIsLoadingProducts(false);
        };
        const fetchInitialBanners = async () => {
            const result = await fetchBannerPage(1);
            if (!result.success && slidesRef.current.length === 0) {
                applyDefaultBannerSlide();
            }
        };
        void fetchInitialBanners();
        fetchCategories();
        fetchProducts();
    }, [applyDefaultBannerSlide, fetchBannerPage]);
    const handleNextSlideClicked = useCallback(() => {
        if (isLoadingBanners) {
            return;
        }
        setSlideDirection(1);
        const loadedSlides = slidesRef.current;
        if (loadedSlides.length === 0) {
            return;
        }
        const current = currentSlideRef.current;
        if (current < loadedSlides.length - 1) {
            setCurrentSlide(current + 1);
            return;
        }
        const loadNextPageIfNeeded = async () => {
            if (hasMoreBannersRef.current) {
                let previousLength = slidesRef.current.length;
                let nextPage = bannerPageRef.current;
                while (hasMoreBannersRef.current) {
                    nextPage += 1;
                    const result = await fetchBannerPage(nextPage);
                    if (!result.success) {
                        break;
                    }
                    if (result.slides.length > previousLength) {
                        setCurrentSlide(previousLength);
                        return;
                    }
                    previousLength = result.slides.length;
                }
            }
            setCurrentSlide(0);
        };
        void loadNextPageIfNeeded();
    }, [fetchBannerPage, isLoadingBanners]);
    const handlePreviousSlideClicked = useCallback(() => {
        if (isLoadingBanners) {
            return;
        }
        setSlideDirection(-1);
        const loadedSlides = slidesRef.current;
        if (loadedSlides.length === 0) {
            return;
        }
        const current = currentSlideRef.current;
        setCurrentSlide(current === 0 ? loadedSlides.length - 1 : current - 1);
    }, [isLoadingBanners]);
    const handleCarouselDialSelected = useCallback((targetSlideIndex) => {
        if (isLoadingBanners || targetSlideIndex < 0 || targetSlideIndex === currentSlideRef.current) {
            return;
        }
        const current = currentSlideRef.current;
        setSlideDirection(targetSlideIndex > current ? 1 : -1);
        const loadedSlides = slidesRef.current;
        if (targetSlideIndex < loadedSlides.length) {
            setCurrentSlide(targetSlideIndex);
            return;
        }
        const loadUntilTarget = async () => {
            let nextPage = bannerPageRef.current;
            while (targetSlideIndex >= slidesRef.current.length && hasMoreBannersRef.current) {
                nextPage += 1;
                await fetchBannerPage(nextPage);
            }
            if (targetSlideIndex < slidesRef.current.length) {
                setCurrentSlide(targetSlideIndex);
            }
        };
        void loadUntilTarget();
    }, [fetchBannerPage, isLoadingBanners]);
    const handleBannerClicked = useCallback(() => {
        const activeSlide = slidesRef.current[currentSlideRef.current];
        const linkTarget = activeSlide?.linkTarget?.trim();
        const linkType = activeSlide?.linkType?.toLowerCase();
        if (!linkTarget) {
            return;
        }
        if (linkType?.includes("category")) {
            const matchingCategory = categories.find((category) => category?._id === linkTarget || category?.identifier === linkTarget);
            const categoryName = matchingCategory?.name?.trim();
            if (categoryName) {
                navigate(`/shop/${linkTarget}/${encodeURI(categoryName)}`);
            }
            else {
                navigate(`/shop/${linkTarget}`);
            }
            return;
        }
        if (linkType?.includes("product")) {
            navigate(`/product/${linkTarget}`);
            return;
        }
        if (/^https?:\/\//i.test(linkTarget)) {
            window.open(linkTarget, "_blank", "noopener,noreferrer");
            return;
        }
        navigate(linkTarget.startsWith("/") ? linkTarget : `/${linkTarget}`);
    }, [categories, navigate]);
    const handleShopCategory = useCallback((id, name) => () => {
        navigate(`/shop/${id}/${encodeURI(name)}`);
    }, [navigate]);
    return useMemo(() => ({
        categories,
        currentSlide,
        slideDirection,
        handleNextSlideClicked,
        handlePreviousSlideClicked,
        handleCarouselDialSelected,
        handleBannerClicked,
        handleShopCategory,
        recommendedItems,
        slides,
        supportingDocuments,
        isLoadingProducts,
        isLoadingCategories,
        isLoadingBanners,
    }), [
        categories,
        currentSlide,
        slideDirection,
        handleNextSlideClicked,
        handlePreviousSlideClicked,
        handleCarouselDialSelected,
        handleBannerClicked,
        handleShopCategory,
        recommendedItems,
        slides,
        supportingDocuments,
        isLoadingProducts,
        isLoadingCategories,
        isLoadingBanners,
    ]);
}
